<template>
    <div>
        <div class="mb-3 flex justify-end gap-x-3">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.adminCP.task_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddTask">
                {{ $t('labels.taskModule.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="tables"
            :headers="headers()"
            :server="apiGetTasks"
            :auto-size="autoSizeTable"
            :use-archive="useTaskArchive"
            :use-restore="useTaskRestore"
            :use-delete="true"
            :delete-function="handleDelete"
            use-selection
            store-name="taskDataTable"
            store-key="Task"
            @selection-changed="onSelectionChanged" />

        <add-task
            ref="addTask"
            @refresh-tables="refreshTables()" />

        <edit-task
            ref="editTask"
            @refresh-tables="refreshTables()" />

        <delete-task
            :show="deleteTask"
            :task="taskSelected"
            @refresh-tables="refreshTables()"
            @close="closeDelete" />

        <delete-task
            :show="deleteTasks"
            :task="taskIdMultiSelected"
            @refresh-tables="refreshTables()"
            @reset-task-selecteds="resetTaskSelecteds()"
            @close="closeDeletes" />

        <detail-task
            ref="detailTask"
            @refresh-tables="refreshTables()" />

        <replicate-task
            ref="replicateTask"
            @refresh-tables="refreshTables()" />
    </div>
</template>

<script lang="ts" setup>
import data from './ignore/imports/data'
import { ITask } from './ignore/imports/form'
import TaskCell from './ignore/tables/task-name.vue'
import AddTask from './ignore/tasks/add.vue'
import DeleteTask from './ignore/tasks/delete.vue'
import DetailTask from './ignore/tasks/detail.vue'
import EditTask from './ignore/tasks/edit.vue'
import ReplicateTask from './ignore/tasks/replicate.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import { $api } from '#imports'
const { contTypes } = data()

const apiGetTasks = $urlAdmin.business.task['get-list']

const { $formatters } = useNuxtApp()

const tables = ref()
const addTask = ref()
const editTask = ref()
const deleteTask = ref<Boolean>(false)
const deleteTasks = ref<Boolean>(false)
const detailTask = ref()
const replicateTask = ref()
const taskSelected = ref<any>()
const taskMultiSelected = ref<Array<ITask> | null>()
const vehicleCategories = ref<null | Array<any>>()

onBeforeMount(() => {
  const api = $urlAdmin.vehicle.category.getAll

  $api(api).then((data) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      vehicleCategories.value = resData.data || []
    }
  })
})

const handleAddTask = () => {
  if (addTask.value) {
    addTask.value.open()
  }
}

const handleEdit = (task: any) => {
  if (editTask.value) {
    editTask.value.open(task._id)
  }
}

const handleReplicate = (task: any) => {
  if (replicateTask.value) {
    replicateTask.value.open(task._id)
  }
}

const handleDelete = (deleteOptions: any) => {
  if (deleteOptions && deleteOptions._id) {
    taskSelected.value = {
      selectedItems: [deleteOptions._id]
    }
  } else {
    taskSelected.value = deleteOptions
  }
  deleteTask.value = true
}

const closeDelete = () => {
  deleteTask.value = false
}

const closeDeletes = () => {
  deleteTasks.value = false
}

const handleViewDetail = (task: any) => {
  if (detailTask.value) {
    detailTask.value.open(task._id)
  }
}

const headers = () => {
  let headersDefault: any = [
    {
      field: 'name',
      headerName: $t('labels.taskModule.name'),
      cellRenderer: TaskCell,
      minWidth: 150,
      filter: 'agTextColumnFilter'
    },
    {
      field: 'taskType',
      headerName: $t('labels.type'),
      minWidth: 150,
      valueGetter: (params: any) => {
        const taskType = params.data.taskType
        if (taskType === 'AFFECT_SHIPPING_TIME') {
          return $t('labels.task')
        }
        if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
          return $t('labels.job')
        }
      }
    },
    {
      field: 'canSplitWay',
      headerName: $t('labels.canSplitWay'),
      minWidth: 150,
      valueGetter: (params: any) => {
        const canSplitWay = params.data.canSplitWay
        const taskType = params.data.taskType

        if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
          return ''
        }

        if (canSplitWay) {
          return $t('labels.true')
        }

        return $t('labels.false')
      }
    },
    {
      field: 'requireContainerNumber',
      headerName: $t('labels.requireContainerNumber'),
      minWidth: 150,
      valueGetter: (params: any) => {
        const requireContainerNumber = params.data.requireContainerNumber
        const taskType = params.data.taskType

        if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
          return ''
        }

        if (requireContainerNumber) {
          return $t('labels.true')
        }

        return $t('labels.false')
      }
    },
    {
      field: 'documentRequired',
      headerName: $t('labels.documentRequired'),
      minWidth: 150,
      valueGetter: (params: any) => {
        const documentRequired = params.data.documentRequired
        const taskType = params.data.taskType

        if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
          return ''
        }

        if (documentRequired) {
          return $t('labels.true')
        }

        return $t('labels.false')
      }
    },
    {
      field: 'taskStartEndDurationRequired',
      headerName: $t('labels.taskStartEndDurationRequired'),
      minWidth: 150,
      valueGetter: (params: any) => {
        const taskStartEndDurationRequired = params.data.taskStartEndDurationRequired
        const taskType = params.data.taskType

        if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
          return ''
        }

        if (taskStartEndDurationRequired) {
          return $t('labels.true')
        }

        return $t('labels.false')
      }
    },
    {
      field: 'allowCustomerViewDocument',
      headerName: $t('labels.allowCustomerViewDocument'),
      minWidth: 150,
      valueGetter: (params: any) => {
        const allowCustomerViewDocument = params.data.allowCustomerViewDocument
        const taskType = params.data.taskType

        if (taskType === 'NO_AFFECT_SHIPPING_TIME') {
          return ''
        }

        if (allowCustomerViewDocument) {
          return $t('labels.true')
        }

        return $t('labels.false')
      }
    },
    {
      field: 'timeConditions',
      headerName: $t('labels.earliestLatest'),
      minWidth: 180,
      valueGetter: (params: any) => {
        let names = ''
        const timeConditions: any[] = params.data.timeConditions

        if (timeConditions && timeConditions.length) {
          timeConditions.forEach((timeCondition: any) => {
            let name: string = timeCondition.name
            if (timeCondition.type === 'START') {
              name += ` - ${$t('labels.start')}`
            } else {
              name += ` - ${$t('labels.end')}`
            }

            if (timeCondition.timeType === 'EARLIEST') {
              name += ` - ${$t('labels.earliest')}`
            } else if (timeCondition.timeType === 'LATEST') {
              name += ` - ${$t('labels.latest')}`
            } else {
              name += ` - ${$t('labels.permanent')}`
            }

            names += names ? `, ${name}` : name
          })
        }
        return names
      }
    }
  ]

  const headersRemooc: any = [
    ...contTypes.map((type) => {
      return {
        headerName: type.label,
        sortable: false,
        minWidth: 150,
        valueGetter: (params: any) => {
          try {
            const taskType = params.data.taskType

            const completionTimes = params.data.completionTimes

            if (!completionTimes) {
              return ''
            }

            const time = completionTimes.contCategory[type.value].averageTime

            if (!time) {
              return ''
            }

            return time + ` ${$t('labels.hour')}`
          } catch (e) {
            return ''
          }
        }
      }
    })
  ]

  const headersVehicle = () => {
    if (!vehicleCategories.value) {
      return []
    }
    return [
      ...vehicleCategories?.value?.map((item: any) => {
        const id = item._id
        return {
          headerName: item.name,
          sortable: false,
          minWidth: 150,
          valueGetter: (params: any) => {
            try {
              const taskType = params.data.taskType

              const completionTimes = params.data.completionTimes

              if (!completionTimes) {
                return ''
              }

              const time = completionTimes.vehicleCategory[id].averageTime

              if (!time) {
                return ''
              }

              return time + ` ${$t('labels.hour')}`
            } catch (e) {
              return ''
            }
          }
        }
      })
    ]
  }

  const headersAction = [
    {
      field: 'description',
      headerName: $t('labels.description'),
      minWidth: 150
    },
    {
      field: 'createdAt',
      headerName: $t('labels.createdAt'),
      sortable: true,
      minWidth: 180,
      valueGetter: (params: any) => {
        if (params.data.createdAt) {
          return $formatters.datetime(params.data.createdAt)
        }
      }
    },
    {
      field: 'updatedAt',
      headerName: $t('labels.updatedAt'),
      sortable: true,
      minWidth: 180,
      valueGetter: (params: any) => {
        if (params.data.updatedAt) {
          return $formatters.datetime(params.data.updatedAt)
        }
      }
    },
    {
      field: 'action',
      pinned: 'right',
      sortable: false,
      lockPosition: true,
      lockPinned: true,
      popup: true,
      suppressRowTransform: true,
      suppressMenu: true,
      resizable: false,
      headerName: $t('labels.action'),
      cellStyle: { overflow: 'visible' },
      cellRenderer: 'Actions',
      cellRendererParams: {
        storeKey: 'taskDataTable',
        storeName: 'Task',
        items: [
          {
            icon: 'hi-eye-icon',
            label: $t('labels.viewDetail'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: true,
            click: handleViewDetail,
            isAll: true
          },
          {
            icon: 'hi-pencil-square-icon',
            label: $t('labels.edit'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.adminCP.task_edit),
            click: handleEdit
          },
          {
            icon: 'hi-document-duplicate-icon',
            label: $t('labels.replicate'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.adminCP.task_add),
            click: handleReplicate
          },
          {
            icon: 'hi-trash-icon',
            label: $t('labels.delete'),
            class: 'text-error-600 hover:text-error-500',
            permissions: checkPermission(layoutPermissions.adminCP.task_delete),
            click: handleDelete,
            isArchive: true
          }
        ]
      }
    }
  ]

  headersDefault = headersDefault.concat(headersVehicle()).concat(headersRemooc).concat(headersAction)

  return headersDefault
}

const autoSizeTable = {
  active: true,
  skipHeader: false
}

const refreshTables = () => {
  if (tables.value) {
    tables.value.refresh()
  }
}

const resetTaskSelecteds = () => {
  taskMultiSelected.value = null
}

const onSelectionChanged = (task: Array<ITask> | null) => {
  taskMultiSelected.value = task
}

const taskIdMultiSelected = computed(() => {
  if (!taskMultiSelected.value || !Array.isArray(taskMultiSelected.value)) {
    return []
  }
  return taskMultiSelected.value.map((task) => task._id)
})

const useTaskArchive = computed(() => {
  return { api: $urlAdmin.business.task.archive, permissions: checkPermission(layoutPermissions.adminCP.task_archive) }
})

const useTaskRestore = computed(() => {
  return { api: $urlAdmin.business.task.restore, permissions: checkPermission(layoutPermissions.adminCP.task_archive) }
})
</script>
